// ============================================================
// Meldungen (Toasts) — eigener, winziger zustand-Speicher.
//
// Bewusst NICHT Teil eines großen useStore: die Toaster-Komponente ist das
// einzige, was hier mitliest, und ein Modul, das nur Meldungen kann, lässt
// sich aus jeder Ebene aufrufen — auch aus lib/fetchJson.ts, das von keinem
// Bildschirm etwas wissen darf.
// ============================================================
import { create } from "zustand";

export type ToastTyp = "success" | "error" | "info" | "warn";

export interface Toast {
  id: string;
  typ: ToastTyp;
  /** die eine fette Zeile */
  msg: string;
  /** zweite, gedämpfte Zeile — Grund, Feldname, Serverantwort */
  detail?: string;
  /** Text hinter „Meldung kopieren"; bei Fehlern von selbst gesetzt */
  copy?: string;
  /** detail in Geist Mono setzen (Kennungen, Serverantworten) */
  mono?: boolean;
  /** Anzeigedauer in ms; 0 heißt: bleibt stehen, bis jemand schließt */
  dauer?: number;
  /** Ein Knopf in der Meldung, etwa „Wiederherstellen" nach dem Löschen einer Karte
   *  (§4.11). Ein Klick führt ihn aus und schließt die Meldung. Der Rückruf darf den
   *  Bildschirm überleben, der ihn gesetzt hat — keine Zustände einer Komponente darin. */
  aktion?: { label: string; onClick: () => void };
}

/** alles außer id und msg ist freiwillig */
export type ToastEingabe = { msg: string } & Partial<Omit<Toast, "id" | "msg">>;

interface ToastState {
  toasts: Toast[];
  addToast(t: ToastEingabe): string;
  removeToast(id: string): void;
  clearToasts(): void;
}

export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],
  addToast(eingabe) {
    const id = Math.random().toString(36).slice(2);
    const basis = { typ: "info" as ToastTyp, ...eingabe };
    const istFehler = basis.typ === "error";
    const t: Toast = {
      ...basis,
      id,
      // Fehler bleiben länger stehen (12 s statt 3,6 s) und tragen immer einen
      // kopierbaren Volltext — wer uns eine Störung meldet, soll den Wortlaut
      // schicken können statt ihn abzutippen.
      dauer: basis.dauer ?? (istFehler ? 12000 : 3600),
      copy: basis.copy ?? (istFehler ? [basis.msg, basis.detail].filter(Boolean).join("\n") : undefined),
    };
    set((s) => ({ toasts: [...s.toasts, t] }));
    if (t.dauer !== 0) {
      setTimeout(() => get().removeToast(id), t.dauer);
    }
    return id;
  },
  removeToast(id) {
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
  },
  clearToasts() {
    set({ toasts: [] });
  },
}));

// ---------------------------------------------------------------------------
// Bequemer Aufruf von überall — auch außerhalb von React (fetchJson, Fehler
// aus einem Hintergrundlauf). getState() statt eines Hooks, deshalb braucht
// es weder Kontext noch Komponente.
//
//   toast("Gespeichert.")
//   toast.error("Konnte nicht speichern.", { detail: e.message })
//   const id = toast.info("Lädt …", { dauer: 0 }); toast.close(id);
// ---------------------------------------------------------------------------
function basis(msg: string, opts: Partial<Omit<Toast, "id" | "msg">> = {}): string {
  return useToasts.getState().addToast({ msg, ...opts });
}

type ToastOpts = Partial<Omit<Toast, "id" | "msg">>;

export const toast = Object.assign(basis, {
  success: (msg: string, opts: ToastOpts = {}) => basis(msg, { ...opts, typ: "success" }),
  error: (msg: string, opts: ToastOpts = {}) => basis(msg, { ...opts, typ: "error" }),
  info: (msg: string, opts: ToastOpts = {}) => basis(msg, { ...opts, typ: "info" }),
  warn: (msg: string, opts: ToastOpts = {}) => basis(msg, { ...opts, typ: "warn" }),
  /** eine stehende Meldung (dauer: 0) wieder wegnehmen */
  close: (id: string) => useToasts.getState().removeToast(id),
  clear: () => useToasts.getState().clearToasts(),
});
